<template>
  <div class="mt-2">
    <!-- Loading Spinner for Conversation Details -->
    <LoadingSpinner :loading="conversationLoading" />
    
    <!-- Filters Card -->
    <div class="card shadow-sm mb-3">
      <div class="card-body">
        <div class="d-flex flex-wrap gap-3 align-items-end">
          <button 
            class="btn btn-outline-primary btn-sm" 
            @click="refreshConversations"
            :disabled="loading"
          >
            <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loading }"></i>
            Refresh
          </button>
          <input 
            type="text" 
            class="form-control form-control-sm" 
            :value="searchTerm" 
            @input="$emit('searchTermChanged', $event.target.value)"
            placeholder="Search by conversation ID or title..."
            :disabled="loading"
            style="width: 220px;"
          />
          <select class="form-select form-select-sm" :value="filters.customerId" @change="$emit('filtersChanged', { ...filters, customerId: $event.target.value })" :disabled="loading" style="width: auto; min-width: 140px;">
            <option value="">All Customers</option>
            <option v-for="customerId in uniqueCustomerIds" :key="customerId" :value="customerId">
              {{ getCustomerName(customerId) || customerId }}
            </option>
          </select>
          <select class="form-select form-select-sm" :value="filters.userId" @change="$emit('filtersChanged', { ...filters, userId: $event.target.value })" :disabled="loading" style="width: auto; min-width: 160px;">
            <option value="">All Users</option>
            <option value="non-admin">Non-admin Users</option>
            <option v-for="userId in uniqueUserIds" :key="userId" :value="userId">
              {{ getUserName(userId) || userId }}
            </option>
          </select>
          <select class="form-select form-select-sm" :value="filters.initiationType" @change="$emit('filtersChanged', { ...filters, initiationType: $event.target.value })" :disabled="loading" style="width: auto; min-width: 120px;">
            <option value="">All Types</option>
            <option v-for="type in uniqueInitiationTypes" :key="type" :value="type">
              {{ formatInitiationType(type) }}
            </option>
          </select>
          <select class="form-select form-select-sm" :value="filters.model" @change="$emit('filtersChanged', { ...filters, model: $event.target.value })" :disabled="loading" style="width: auto; min-width: 160px;">
            <option value="">All Models</option>
            <option v-for="model in uniqueModels" :key="model" :value="model">
              {{ model }}
            </option>
          </select>
          <div class="d-flex align-items-center gap-1">
            <select class="form-select form-select-sm" :value="dateFilter" @change="$emit('dateFilterChanged', $event.target.value)" :disabled="loading" style="width: auto; min-width: 120px;">
              <option value="all">All Time</option>
              <option value="yesterday">Yesterday</option>
              <option value="last3">Last 3 Days</option>
              <option value="last7">Last 7 Days</option>
              <option value="last30">Last 30 Days</option>
              <option value="custom">Custom</option>
            </select>
            <div v-if="dateFilter === 'custom'" class="position-relative">
              <Datepicker 
                :model-value="dates" 
                :range="true" 
                :clearable="false"
                :enableTimePicker="false" 
                :disabled="loading"
                @blur="onDateSelected"
                @update:model-value="onCustomDatesChanged"
                class="form-control-sm"
                style="width: 200px;"
              />
            </div>
          </div>
          <div class="btn-group ms-auto" role="group" aria-label="Smart Search version">
            <button
              type="button"
              class="btn btn-sm"
              :class="sessionsVersion === '1.0' ? 'btn-primary' : 'btn-outline-primary'"
              @click="$emit('sessionsVersionChanged', '1.0')"
            >
              1.0
            </button>
            <button
              type="button"
              class="btn btn-sm"
              :class="sessionsVersion === '2.0' ? 'btn-primary' : 'btn-outline-primary'"
              @click="$emit('sessionsVersionChanged', '2.0')"
            >
              2.0
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Conversation Count -->
    <div v-if="conversations.length > 0" class="px-2 mb-2 text-muted small">
      <span v-if="searchTerm || filters.customerId || filters.userId || filters.initiationType || filters.model || dateFilter !== 'all'">
        {{ filteredConversations.length }} of {{ conversations.length }} conversations
      </span>
      <span v-else>
        {{ conversations.length }} conversations
      </span>
    </div>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary mb-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="text-primary">Loading conversations...</div>
    </div>

    <div v-else-if="conversations.length === 0" class="py-3">
      <p class="text-muted">No conversations found.</p>
    </div>

    <div v-else-if="filteredConversations.length === 0" class="py-3">
      <p class="text-muted">No conversations match your search and filter criteria.</p>
    </div>

    <div v-else class="table-responsive table-container scrollable-container">
      <table class="table table-hover">
        <thead class="bg-gray-500 text-uppercase sticky-header">
          <tr>
            <th style="width: 80px;" class="sortable-header" @click="toggleSort('id')">
              ID <font-awesome-icon :icon="getSortIcon('id')" :class="getSortIconClass('id')" />
            </th>
            <th style="width: 350px;">Title</th>
            <th style="width: 120px;" class="sortable-header" @click="toggleSort('customer')">
              Customer <font-awesome-icon :icon="getSortIcon('customer')" :class="getSortIconClass('customer')" />
            </th>
            <th style="width: 80px;" class="sortable-header" @click="toggleSort('user')">
              User <font-awesome-icon :icon="getSortIcon('user')" :class="getSortIconClass('user')" />
            </th>
            <th style="width: 140px;">Initiation Type</th>
            <th style="width: 200px;">Model</th>
            <th style="width: 180px;" class="sortable-header" @click="toggleSort('created')">
              Created <font-awesome-icon :icon="getSortIcon('created')" :class="getSortIconClass('created')" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="conversation in filteredConversations" 
            :key="conversation.conversationId"
            class="clickable-row"
            :class="{ 'loading-row': conversationLoading }"
            @click="viewConversation(conversation)"
            :style="conversationLoading ? 'cursor: not-allowed;' : ''"
          >
            <td>
              <strong>{{ conversation.conversationId }}</strong>
            </td>
            <td>
              <div 
                class="text-truncate" 
                style="max-width: 380px;"
                :data-bs-toggle="shouldShowTooltip(conversation.conversationContext?.title) ? 'tooltip' : null"
                data-bs-placement="top"
                :title="shouldShowTooltip(conversation.conversationContext?.title) ? (conversation.conversationContext?.title || 'Untitled') : null"
              >
                {{ conversation.conversationContext?.title || 'Untitled' }}
              </div>
            </td>
            <td>
              {{ getCustomerName(conversation.customerId) || '-' }}
            </td>
            <td>
              {{ getUserName(conversation.userId) || '-' }}
            </td>
            <td>
              {{ conversation.metaData?.initiationType ? formatInitiationType(conversation.metaData.initiationType) : '-' }}
            </td>
            <td>
              {{ conversation.model || '-' }}
            </td>
            <td>
              <div class="text-truncate" style="max-width: 160px;">
                {{ formatTimestamp(conversation.createdAt) }}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import InsightsService from '../../services/InsightsService.js';
import dayjs from 'dayjs';
import Datepicker from 'vue3-date-time-picker';
import 'vue3-date-time-picker/dist/main.css';
import AppUtil from '../../utils/AppUtil.js';
import LoadingSpinner from '../../components/LoadingSpinner.vue';

export default {
  name: "ConversationsView",
  components: {
    LoadingSpinner,
    Datepicker,
  },
  props: {
    viewModel: {
      type: Object,
      required: true,
    },
    sessionsVersion: {
      type: String,
      required: true,
    },
    // Filter props from parent
    filters: {
      type: Object,
      required: true,
      default: () => ({
        customerId: '',
        userId: '',
        initiationType: '',
        model: '',
      }),
    },
    searchTerm: {
      type: String,
      required: true,
      default: '',
    },
    dateFilter: {
      type: String,
      required: true,
      default: 'all',
    },
    startDate: {
      type: Date,
      required: true,
      default: null,
    },
    endDate: {
      type: Date,
      required: true,
      default: null,
    },
    dates: {
      type: Array,
      required: true,
      default: () => [],
    },
    sorting: {
      type: Object,
      required: true,
      default: () => ({
        field: null,
        direction: null,
      }),
    },
  },
  data() {
    return {
      model: streetmetrics.model,
      conversations: [],
      loading: false,
      conversationsFetched: false,
      insightsService: new InsightsService(),
      appUtil: new AppUtil(),
      conversationLoading: false,
    };
  },
  computed: {
    uniqueFilterOptions() {
      const customerIds = new Set();
      const userIds = new Set();
      const initiationTypes = new Set();
      const models = new Set();

      this.conversations.forEach(conversation => {
        if (conversation.customerId) customerIds.add(conversation.customerId);
        if (conversation.userId) userIds.add(conversation.userId);
        if (conversation.metaData?.initiationType) initiationTypes.add(conversation.metaData.initiationType);
        if (conversation.model) models.add(conversation.model);
      });
      
      return {
        customerIds: [...customerIds].sort((a, b) => a - b),
        userIds: [...userIds].sort((a, b) => a - b),
        initiationTypes: [...initiationTypes].sort(),
        models: [...models].sort(),
      };
    },
    uniqueCustomerIds() {
      return this.uniqueFilterOptions.customerIds;
    },
    uniqueUserIds() {
      let userIds = this.uniqueFilterOptions.userIds;
      if (this.filters.customerId) {
        userIds = userIds.filter(userId => {
          return this.conversations.some(c => 
            c.userId === userId && c.customerId == this.filters.customerId
          );
        });
      }
      return userIds;
    },
    uniqueInitiationTypes() {
      return this.uniqueFilterOptions.initiationTypes;
    },
    uniqueModels() {
      return this.uniqueFilterOptions.models;
    },
    
    // Filtered conversations with real-time filtering
    filteredConversations() {
      let filtered = this.conversations;
      
      // Apply search filter (ID or title)
      if (this.searchTerm.trim()) {
        const searchLower = this.searchTerm.toLowerCase().trim();
        filtered = filtered.filter(conversation => {
          const id = conversation.conversationId?.toString() || '';
          const title = conversation.conversationContext?.title || '';
          return id.includes(searchLower) || title.toLowerCase().includes(searchLower);
        });
      }
      
      // Apply dropdown filters
      if (this.filters.customerId) {
        filtered = filtered.filter(c => c.customerId == this.filters.customerId);
      }
      if (this.filters.userId === 'non-admin') {
        filtered = filtered.filter(c => !this.isAdminUser(c.userId) && Number(c.customerId) !== 1);
      } else if (this.filters.userId) {
        filtered = filtered.filter(c => c.userId == this.filters.userId);
      }
      if (this.filters.initiationType) {
        filtered = filtered.filter(c => c.metaData?.initiationType === this.filters.initiationType);
      }
      if (this.filters.model) {
        filtered = filtered.filter(c => c.model === this.filters.model);
      }
      
      // Apply date filter
      if (this.startDate && this.endDate) {
        filtered = filtered.filter(conversation => {
          const createdAt = new Date(conversation.createdAt);
          return createdAt >= this.startDate && createdAt <= this.endDate;
        });
      }
      
      // Apply sorting
      if (this.sorting.field && this.sorting.direction) {
        filtered = [...filtered].sort((a, b) => {
          let aVal, bVal;
          
          if (this.sorting.field === 'id') {
            aVal = parseInt(a.conversationId) || 0;
            bVal = parseInt(b.conversationId) || 0;
          } else if (this.sorting.field === 'created') {
            aVal = new Date(a.createdAt);
            bVal = new Date(b.createdAt);
          } else if (this.sorting.field === 'customer') {
            aVal = this.getCustomerName(a.customerId) || '';
            bVal = this.getCustomerName(b.customerId) || '';
          } else if (this.sorting.field === 'user') {
            aVal = this.getUserName(a.userId) || '';
            bVal = this.getUserName(b.userId) || '';
          }
          
          if (this.sorting.direction === 'asc') {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          } else {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          }
        });
      }
      
      return filtered;
    },
  },
  watch: {
    'filters.customerId'(newCustomerId, oldCustomerId) {
      // If customer changed and user filter is set, clear the user filter
      // since the user might not exist at the new company
      if (oldCustomerId && newCustomerId !== oldCustomerId && this.filters.userId && this.filters.userId !== 'non-admin') {
        const updatedFilters = { ...this.filters, userId: '' };
        this.$emit('filtersChanged', updatedFilters);
      }
    },
    // Watch for when this component becomes active
    'viewModel.childView'(newView) {
      if (newView === 'conversations' && !this.conversationsFetched) {
        this.fetchConversations();
      }
      
      // Hide spinner when navigating away from conversations view
      if (newView !== 'conversations') {
        this.conversationLoading = false;
      }
      
      // Reset spinner state when returning to conversations view
      if (newView === 'conversations') {
        this.conversationLoading = false;
      }
    }
  },
  methods: {
    async fetchConversations() {
      this.loading = true;
      try {
        const response = await this.insightsService.getAdminConversations();

        // Extract conversations array from nested structure: response.data.data
        let data = [];
        if (response && response.data && response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (response && response.data && Array.isArray(response.data)) {
          data = response.data;
        } else if (response && Array.isArray(response)) {
          data = response;
        } else {
          console.error('Unexpected response structure. Expected: {data: {data: [...]}}, got:', response);
          data = [];
        }
                
        this.conversations = data;
        this.conversationsFetched = true;
        
        // Initialize tooltips after data is loaded
        this.$nextTick(() => {
          this.initializeTooltips();
        });
      } catch (error) {
        console.error('Error fetching conversations:', error);
        this.conversations = []; // Ensure it's always an array
      } finally {
        this.loading = false;
      }
    },
    async refreshConversations() {
      this.conversationsFetched = false;
      await this.fetchConversations();
    },
    formatTimestamp(timestamp) {
      return dayjs(timestamp).format('MMM D, YYYY h:mm A');
    },
    formatInitiationType(type) {
      if (!type) return '';
      return type.replace(/_/g, ' ').toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    async viewConversation(conversation) {
      // Prevent multiple simultaneous requests
      if (this.conversationLoading) {
        return;
      }
      
      this.conversationLoading = true;
      try {
        const details = await this.insightsService.getConversationDetails(conversation.conversationId);
        this.$emit('conversationSelected', { conversation, details });
      } catch (error) {
        console.error('Error fetching conversation details:', error);
        this.conversationLoading = false;
      }
    },
    initializeTooltips() {
      if (window.bootstrap && window.bootstrap.Tooltip) {
        const existingTooltips = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        existingTooltips.forEach(function (tooltipEl) {
          const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipEl);
          if (existingTooltip) {
            existingTooltip.dispose();
          }
        });
        const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        tooltipTriggerList.forEach(function (tooltipTriggerEl) {
          new window.bootstrap.Tooltip(tooltipTriggerEl);
        });
      }
    },
    shouldShowTooltip(title) {
      return title && title.length > 50;
    },
    toggleSort(field) {
      let newSorting = { ...this.sorting };
      
      if (newSorting.field === field) {
        // Already sorting by this field, cycle through: asc -> desc -> none
        if (newSorting.direction === 'asc') {
          newSorting.direction = 'desc';
        } else if (newSorting.direction === 'desc') {
          newSorting.field = null;
          newSorting.direction = null;
        }
      } else {
        // New field, start with ascending
        newSorting.field = field;
        newSorting.direction = 'asc';
      }
      
      this.$emit('sortingChanged', newSorting);
    },
    getSortIcon(field) {
      if (this.sorting.field !== field) {
        return 'sort';
      }
      return this.sorting.direction === 'asc' ? 'sort-up' : 'sort-down';
    },
    getSortIconClass(field) {
      if (this.sorting.field !== field) {
        return 'text-muted';
      }
      return 'text-primary';
    },
    onCustomDatesChanged(newDates) {
      if (newDates && newDates.length === 2) {
        this.$emit('customDatesChanged', newDates);
      }
    },
    getUserName(userId) {
      if (!userId || !this.model.users) return null;
      const user = this.model.users.find(u => u.userId === userId);
      return user ? user.displayName : null;
    },
    isAdminUser(userId) {
      return this.getUserName(userId) === 'Admin';
    },
    getCustomerName(customerId) {
      if (!customerId || !this.model.customers) return null;
      const customer = this.model.customers.find(c => c.customerId === customerId);
      return customer ? customer.customerName : null;
    },
  },
  mounted() {
    if (this.viewModel.childView === 'conversations' && !this.conversationsFetched) {
      this.fetchConversations();
    }
  },
};
</script>

<style scoped>
.table-container {
  max-height: 70vh;
}

.scrollable-container {
  overflow-y: auto;
}

.form-label {
  white-space: nowrap;
  margin-bottom: 0;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s ease-in-out, text-shadow 0.2s ease-in-out;
  text-shadow: 0 0 0 rgba(0, 0, 0, 0);
}

.clickable-row:hover {
  background-color: rgba(0, 123, 255, 0.1);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15);
  text-shadow: 0 0 1px rgba(0, 0, 0, 0.3);
}

.loading-row {
  opacity: 0.6;
  pointer-events: none;
}

.loading-row:hover {
  background-color: inherit !important;
  box-shadow: none !important;
  text-shadow: none !important;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

.sticky-header th {
  position: sticky;
  top: 0;
  background-color: inherit;
  z-index: 10;
}
</style> 